import { clientAppFetch } from './clientAppFetch';
import { isClientAppChannelEnabled } from './clientAppAccess';
import { clientIdentityFields, hasClientIdentity, resolveClientIdentity } from './clientIdentity';
import { supabase } from '../supabaseClient';

const VALID_SYNC_STATES = new Set(['pending', 'synced', 'error']);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class NextRdvSyncError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'NextRdvSyncError';
    this.code = code;
  }
}

const sameNullableInstant = (left, right) => {
  if (left === null && right === null) return true;
  const leftMs = Date.parse(left || '');
  const rightMs = Date.parse(right || '');
  return Number.isFinite(leftMs) && Number.isFinite(rightMs) && leftMs === rightMs;
};

export function shouldSyncNextRdvToClientApp(client, journey) {
  return isClientAppChannelEnabled(client, journey);
}

export async function saveNextRdvInSaasOnly(clientId, targetAt, {
  expectedCurrentAt = null,
  db = supabase,
} = {}) {
  const normalizedClientId = String(clientId || '').trim();
  if (!UUID_RE.test(normalizedClientId)) {
    throw new NextRdvSyncError('identity_missing', 'La fiche SaaS de la cliente est introuvable.');
  }
  if (targetAt !== null && !Number.isFinite(Date.parse(targetAt || ''))) {
    throw new Error('Date de rendez-vous invalide.');
  }
  if (expectedCurrentAt !== null && !Number.isFinite(Date.parse(expectedCurrentAt || ''))) {
    throw new Error('Version actuelle du rendez-vous invalide.');
  }
  const { data, error } = await db.rpc('enregistrer_prochain_rdv_document_atomiquement', {
    p_client_id: normalizedClientId,
    p_target_at: targetAt,
    p_expected_current_at: expectedCurrentAt,
  });
  if (error) {
    throw new Error(error.message || "Le rendez-vous n'a pas pu etre enregistre dans le SaaS.");
  }
  const confirmationKeys = [
    'client_id',
    'delivery_mode',
    'mode',
    'next_rdv_app_sync',
    'next_rdv_at',
    'ok',
  ];
  const hasExactConfirmationShape = data !== null
    && typeof data === 'object'
    && !Array.isArray(data)
    && Object.keys(data).sort().join('|') === confirmationKeys.join('|');
  if (!hasExactConfirmationShape
      || data.ok !== true
      || data?.client_id !== normalizedClientId
      || data?.mode !== 'saas_only'
      || data?.delivery_mode !== 'document'
      || !sameNullableInstant(data.next_rdv_at, targetAt)
      || data.next_rdv_app_sync !== null) {
    throw new Error("Le rendez-vous n'a pas été confirmé dans le SaaS.");
  }
  return {
    mode: 'saas_only',
    next_rdv_at: data.next_rdv_at ?? null,
  };
}

export function buildNextRdvSyncMarker({
  status,
  targetAt,
  requestId,
  requestedAt,
  at = requestedAt,
  errorCode = null,
} = {}) {
  if (!VALID_SYNC_STATES.has(status)) throw new Error('Statut de synchronisation RDV invalide.');
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId || '')) {
    throw new Error('Identifiant de synchronisation RDV invalide.');
  }
  if (targetAt !== null && !Number.isFinite(Date.parse(targetAt || ''))) {
    throw new Error('Date de rendez-vous invalide.');
  }
  if (!Number.isFinite(Date.parse(requestedAt || '')) || !Number.isFinite(Date.parse(at || ''))) {
    throw new Error('Horodatage de synchronisation RDV invalide.');
  }
  const marker = {
    version: 1,
    status,
    request_id: requestId,
    target_at: targetAt,
    requested_at: requestedAt,
    updated_at: at,
  };
  if (status === 'error') marker.error_code = errorCode || 'client_app_sync_failed';
  return marker;
}

export function assertNextRdvSyncConfirmation(response, client, targetAt, requestId, requestedAt) {
  const expectedClientId = resolveClientIdentity(client).clientId;
  if (response?.ok !== true || typeof response?.client_id !== 'string' || !response.client_id.trim()) {
    throw new NextRdvSyncError('confirmation_invalid', "L'app cliente n'a pas confirmé la mise à jour du rendez-vous.");
  }
  if (expectedClientId && response.client_id !== expectedClientId) {
    throw new NextRdvSyncError('confirmation_invalid', 'La confirmation concerne une autre cliente.');
  }
  if (response?.rdv_sync_request_id !== requestId || !sameNullableInstant(response?.rdv_sync_requested_at, requestedAt)) {
    throw new NextRdvSyncError('confirmation_invalid', "L'app cliente n'a pas confirmé la bonne version du rendez-vous.");
  }
  if (!sameNullableInstant(response?.journey?.rdv_scheduled_at ?? undefined, targetAt)) {
    throw new NextRdvSyncError('confirmation_invalid', "La date confirmée par l'app cliente ne correspond pas.");
  }
  return response;
}

export async function syncNextRdvToClientApp(
  client,
  targetAt,
  { requestId, requestedAt, fetchApi = clientAppFetch } = {},
) {
  if (!hasClientIdentity(client)) {
    throw new NextRdvSyncError('identity_missing', "Aucun identifiant d'app cliente n'est disponible.");
  }
  let response;
  try {
    response = await fetchApi('/api/admin/client-journey-status', {
      method: 'POST',
      payload: {
        ...clientIdentityFields(client),
        rdv_scheduled_at: targetAt,
        rdv_sync_request_id: requestId,
        rdv_sync_requested_at: requestedAt,
      },
    });
  } catch (error) {
    if (error instanceof NextRdvSyncError) throw error;
    throw new NextRdvSyncError(
      error?.status === 409 ? 'stale_request' : 'client_app_sync_failed',
      error?.message || "La synchronisation de l'app cliente a échoué.",
    );
  }
  return assertNextRdvSyncConfirmation(response, client, targetAt, requestId, requestedAt);
}

function assertPendingMarker(value, requestId, targetAt) {
  if (typeof value?.requested_at !== 'string' || typeof value?.updated_at !== 'string') {
    throw new Error('La préparation de synchronisation RDV est invalide.');
  }
  const expected = buildNextRdvSyncMarker({
    status: 'pending',
    targetAt,
    requestId,
    requestedAt: value?.requested_at,
    at: value?.updated_at,
  });
  if (
    value?.version !== expected.version
    || value?.status !== expected.status
    || value?.request_id !== expected.request_id
    || !sameNullableInstant(value?.target_at ?? null, expected.target_at)
  ) throw new Error('La préparation de synchronisation RDV est invalide.');
  return value;
}

export async function startNextRdvSync(clientId, targetAt, {
  db = supabase,
  requestId = crypto.randomUUID(),
} = {}) {
  const { data, error } = await db.rpc('demarrer_sync_prochain_rdv_atomiquement', {
    p_client_id: clientId,
    p_target_at: targetAt,
    p_request_id: requestId,
  });
  if (error) throw new Error(error.message || 'La synchronisation RDV ne peut pas démarrer.');
  return assertPendingMarker(data, requestId, targetAt);
}

export async function finishNextRdvSync(clientId, marker, status, errorCode = null, { db = supabase } = {}) {
  if (marker?.status !== 'pending' || !marker?.request_id) {
    throw new Error('Marqueur pending RDV requis.');
  }
  const { data, error } = await db.rpc('finaliser_sync_prochain_rdv_atomiquement', {
    p_client_id: clientId,
    p_request_id: marker.request_id,
    p_status: status,
    p_error_code: status === 'error' ? errorCode : null,
  });
  if (error) throw new Error(error.message || 'La synchronisation RDV ne peut pas être finalisée.');
  if (data?.request_id !== marker.request_id || data?.status !== status) {
    throw new Error('La finalisation de synchronisation RDV est invalide.');
  }
  return data;
}

/**
 * Chemin commun pour les anciens et nouveaux ecrans RDV. La version vient
 * toujours de statement_timestamp() dans la RPC SaaS, jamais de l'horloge du
 * navigateur.
 */
export async function syncNextRdvWithServerVersion(client, targetAt, {
  db = supabase,
  fetchApi = clientAppFetch,
  requestId = crypto.randomUUID(),
} = {}) {
  const saasClientId = String(client?.id || '').trim();
  if (!saasClientId) {
    throw new NextRdvSyncError('identity_missing', 'La fiche SaaS de la cliente est introuvable.');
  }
  const pending = await startNextRdvSync(saasClientId, targetAt, { db, requestId });
  try {
    const response = await syncNextRdvToClientApp(client, targetAt, {
      requestId: pending.request_id,
      requestedAt: pending.requested_at,
      fetchApi,
    });
    await finishNextRdvSync(saasClientId, pending, 'synced', null, { db });
    return response;
  } catch (error) {
    const errorCode = error instanceof NextRdvSyncError
      ? error.code
      : 'client_app_sync_failed';
    try {
      await finishNextRdvSync(saasClientId, pending, 'error', errorCode, { db });
    } catch {
      // La requete suivante remplacera atomiquement le marqueur pending.
    }
    throw error;
  }
}
